/**
 * @overview Avatar component
 */

import cx from 'classnames';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { Image } from '../image/index';
import { Typography } from '../typography';
import styles from './avatar.module.scss';
import { getRandomBgClassNameFromName } from './avatar-util';
import { AvatarGrid } from './grid';
import { AvatarTray } from './tray';

export type AvatarSize = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
export type NamePositions = 'right' | 'bottom';

export interface AvatarProps {
  /**
   * Name of the user or business
   */
  name: string;

  /**
   * unique id
   */
  id?: string;

  /**
   * Avatar container class name
   */
  className?: string;

  /**
   * Source of the image
   * @default ''
   */
  src?: string;

  /**
   * If true, name will be displayed next to the avatar
   * @default false
   */
  showName?: boolean;

  /**
   * Position of the avatar name
   * @default right
   */
  namePosition?: NamePositions;

  /**
   * Size of the avatar
   * @default 'medium'
   */
  size?: AvatarSize;

  /**
   * disabled state of the avatar
   * @default false
   */
  disabled?: boolean;

  /**
   * a string combined with the name e.g 'by Author'.
   * Note: nameWithPrefix is only shown when showName is true.
   * @default ''
   */
  nameWithPrefix?: string;

  /**
   * By default tooltip text will not show nameWithPrefix,
   * if this flag is true, it'll show for tooltip as well
   * @default: false
   */
  showNameWithPrefixOnTooltip?: boolean;

  /**
   * Whether the name displayed alongside avatar is a link
   * @default false
   */
  isNameLink?: boolean;

  /**
   * Whether to show tooltip on hover of avatar
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Value of the notification badge.
   * Max displayed value will be 99.
   * @default: undefined.
   */
  badgeValue?: number;

  /**
   * Whether to show a dot badge instead of a numbered badge.
   * @default: false
   */
  showBadge?: boolean;

  /**
   * Number of additional avatars as a string.
   * @default ''
   */
  suffixText?: string;

  /**
   * Whether to show Tooltip along with ellipsis.
   * @default false
   */
  showTooltipOnEllipsis?: boolean;
}

type AvatarTypes<P> = React.FC<P> & {
  Grid: typeof AvatarGrid;
  Tray: typeof AvatarTray;
};

function onError(setImageExist: React.Dispatch<React.SetStateAction<boolean>>) {
  setImageExist(false);
}

export const Avatar: AvatarTypes<AvatarProps> = ({
  name,
  id,
  className = '',
  src = '',
  showName = false,
  namePosition = 'right',
  size = 'm',
  disabled = false,
  nameWithPrefix = '',
  showNameWithPrefixOnTooltip = false,
  isNameLink = false,
  showTooltip = true,
  badgeValue = undefined,
  showBadge = false,
  suffixText = '',
  showTooltipOnEllipsis = false,
  ...restProps
}: AvatarProps) => {
  const rootClassNames = cx(
    styles.root,
    className,
    styles[`size-${size}`],
    namePosition === 'right' ? styles.nameRight : styles.nameBottom,
    {
      [styles.disabled]: disabled,
    }
  );
  const [isImageExists, setIsImageExists] = React.useState(!!src);

  useEffect(() => {
    setIsImageExists(!!src);
  }, [src]);

  const [isImageLoaded, setIsImageLoaded] = React.useState(false);
  const randomBgClassName = getRandomBgClassNameFromName(id || name);
  const tooltipProps = showTooltip
    ? {
        'data-tooltip-content':
          showNameWithPrefixOnTooltip && nameWithPrefix ? nameWithPrefix : name,
        'data-tooltip-position': 'center',
        'data-tooltip-align': 'top',
      }
    : {};

  const renderText = () => {
    const variants = {
      xs: 'body-large',
      s: 'footnote',
      m: 'body-normal',
      l: 'body-large',
    };

    const variantName = variants[size];

    return (
      showName &&
      name && (
        <>
          <Typography
            variant={variantName as any}
            className={cx(styles.name, {
              [styles.nameLink]: isNameLink,
            })}
            ellipsis={showTooltipOnEllipsis ? { rows: 1 } : undefined}
          >
            {nameWithPrefix || name}
          </Typography>
          {suffixText && (
            <Typography variant={variantName as any} noMargin>
              &nbsp;+{suffixText}
            </Typography>
          )}
        </>
      )
    );
  };

  return (
    <div id={id} className={rootClassNames} {...restProps}>
      <div className={styles.avatarContainer}>
        <div className={styles.avatar} {...tooltipProps}>
          {name && (!isImageExists || !isImageLoaded) && (
            <span className={cx(styles.initial, styles[randomBgClassName])}>{name[0]}</span>
          )}
          {src && isImageExists && (
            <Image
              loading="lazy"
              className={cx(styles.avatarImage, {
                [styles.loaded]: isImageLoaded,
              })}
              alt={name}
              onImageLoadError={() => {
                onError(setIsImageExists);
              }}
              onLoad={() => {
                setIsImageLoaded(true);
              }}
              src={src}
            />
          )}
        </div>
        {/* Numbered Badge */}
        {!_.isUndefined(badgeValue) && (
          <span className={cx(styles.badge)}>
            {badgeValue && badgeValue < 100 ? badgeValue : 99}
          </span>
        )}

        {/* Simple Dot Badge */}
        {showBadge && _.isUndefined(badgeValue) && (
          <span className={cx(styles.badge, styles.dotBadge)} />
        )}
      </div>
      {showTooltipOnEllipsis ? (
        <div className={cx(styles.textWrapper)}>{renderText()}</div>
      ) : (
        renderText()
      )}
    </div>
  );
};
Avatar.Grid = AvatarGrid;
Avatar.Tray = AvatarTray;
